// AllocateFundsDialog.js - Simplified version with advance payment preview
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  InputAdornment,
  Box,
  Divider,
  Alert,
  Paper,
  Chip
} from '@mui/material';
import Others from './Others';
import PerformanceWarningDialog from './PerformanceWarningDialog';
import { calculateAdvancePaymentSplit, formatCurrency } from './advancePaymentUtils';

const AllocateFundsDialog = ({ open, onClose, availableFunds, memberDetails, onAllocate }) => {
  const [allocation, setAllocation] = useState({
    savings: 0,
    loan_repayment: 0,
    registration_fee: 0,
    amount_for_advance_payment: 0
  });

  const [othersData, setOthersData] = useState({
    items: [],
    totalAmount: 0,
    descriptions: ''
  });

  // New state for performance warning
  const [performanceWarning, setPerformanceWarning] = useState({
    showWarning: false,
    warningData: null,
    pendingAllocation: null
  });

  // Loading state for submission
  const [submitting, setSubmitting] = useState(false);

  // State for advance payment preview
  const [advancePreview, setAdvancePreview] = useState(null);

  // Get advance loan balance from memberDetails
  const currentAdvanceBalance = memberDetails?.advance_loan_balance || 0;

  // Calculate total and remaining funds
  const totalAllocated = Object.values(allocation).reduce((sum, value) => sum + value, 0) + othersData.totalAmount;
  const remaining = availableFunds - totalAllocated;

  // Handle input change for main allocation fields
  const handleChange = (field, value) => {
    const numValue = parseFloat(value) || 0;
    setAllocation({
      ...allocation,
      [field]: numValue
    });
  };

  // Handle others data change
  const handleOthersChange = (data) => {
    setOthersData(data);
  };

  // Update advance preview when advance payment amount changes
  useEffect(() => {
    if (currentAdvanceBalance > 0) {
      const preview = calculateAdvancePaymentSplit(currentAdvanceBalance, allocation.amount_for_advance_payment);
      setAdvancePreview(preview);
    } else {
      setAdvancePreview(null);
    }
  }, [allocation.amount_for_advance_payment, currentAdvanceBalance]);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 2
    }).format(amount);
  };

  // Validate form before submission - Simplified version
  const validateForm = () => {
    // Total allocation must not exceed available funds
    if (remaining < 0 || totalAllocated <= 0) {
      return false;
    }

    // Check advance payment validity
    if (advancePreview && !advancePreview.is_valid) {
      return false;
    }

    // Check if others data has valid amounts for all items
    if (othersData.items.some(item => !item.amount || item.amount <= 0)) {
      return false;
    }

    return true;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
  
    const allocationData = {
      savings: allocation.savings,
      loan_repayment: allocation.loan_repayment,
      registration_fee: allocation.registration_fee,
      amount_for_advance_payment: allocation.amount_for_advance_payment,
      other: othersData.totalAmount,  
      other_description: othersData.descriptions,
      other_items: othersData.items,
      confirmed: false // First submission without confirmation
    };
  
    try {
      console.log('Submitting allocation data:', allocationData);
      const response = await onAllocate(allocationData);
      console.log('Response from onAllocate:', response);
      
      // If we get here and response indicates success, close the dialog
      if (response && (response.success || response.confirmed)) {
        console.log('Allocation successful, closing dialog');
        onClose();
      }
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      
      // Check if this is a performance warning that needs confirmation
      if (error.response?.data?.warning && error.response?.data?.needs_confirmation) {
        console.log('Showing performance warning dialog');
        setPerformanceWarning({
          showWarning: true,
          warningData: error.response.data,
          pendingAllocation: allocationData
        });
      } else {
        // Handle other errors normally
        const errorMessage = error.response?.data?.error || error.message || "Failed to allocate funds";
        alert(`Error: ${errorMessage}`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Handle continuing after performance warning
  const handleContinueAfterWarning = async () => {
    if (!performanceWarning.pendingAllocation) {
      return;
    }

    setSubmitting(true);
    
    const confirmedAllocation = {
      ...performanceWarning.pendingAllocation,
      confirmed: true // Set confirmed flag to true
    };

    try {
      console.log('Submitting confirmed allocation:', confirmedAllocation);
      const response = await onAllocate(confirmedAllocation);
      console.log('Confirmed allocation response:', response);
      
      // Close both dialogs on success
      setPerformanceWarning({
        showWarning: false,
        warningData: null,
        pendingAllocation: null
      });
      
      // Close the main dialog
      onClose();
      
    } catch (error) {
      // Handle any errors that occur even after confirmation
      console.error('Error during confirmed allocation:', error);
      const errorMessage = error.response?.data?.error || error.message || "Failed to allocate funds after confirmation";
      alert(`Error: ${errorMessage}`);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle canceling the performance warning
  const handleCancelWarning = () => {
    setPerformanceWarning({
      showWarning: false,
      warningData: null,
      pendingAllocation: null
    });
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>Allocate Funds for {memberDetails.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Available funds: {formatCurrency(availableFunds)}
            </Typography>

            {currentAdvanceBalance > 0 && (
              <Typography variant="subtitle2" color="text.secondary">
                Current advance loan balance: {formatCurrency(currentAdvanceBalance)}
              </Typography>
            )}
          </Box>

          {/* Main allocation fields */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Allocate to Savings"
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">KES</InputAdornment>,
                }}
                value={allocation.savings || ''}
                onChange={(e) => handleChange('savings', e.target.value)}
                disabled={submitting}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Allocate to Loan Repayment"
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">KES</InputAdornment>,
                }}
                value={allocation.loan_repayment || ''}
                onChange={(e) => handleChange('loan_repayment', e.target.value)}
                disabled={submitting}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Advance Payments"
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">KES</InputAdornment>,
                }}
                value={allocation.amount_for_advance_payment || ''}
                onChange={(e) => handleChange('amount_for_advance_payment', e.target.value)}
                disabled={submitting}
                helperText="Backend will determine advance payment and interest allocation"
                error={advancePreview && !advancePreview.is_valid}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Allocate to Registration Fee"
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">KES</InputAdornment>,
                }}
                value={allocation.registration_fee || ''}
                onChange={(e) => handleChange('registration_fee', e.target.value)}
                disabled={submitting}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Advance Payment Preview */}
          {advancePreview && allocation.amount_for_advance_payment > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Advance Payment Preview
              </Typography>
              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">
                      Principal Payment
                    </Typography>
                    <Typography variant="h6">
                      {formatCurrency(advancePreview.pay_advance)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">
                      Interest Payment
                    </Typography>
                    <Typography variant="h6">
                      {formatCurrency(advancePreview.pay_advance_interest)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">
                      Remaining Balance
                    </Typography>
                    <Typography variant="h6" color={advancePreview.remaining_balance === 0 ? 'success.main' : 'text.primary'}>
                      {formatCurrency(advancePreview.remaining_balance)}
                      {advancePreview.remaining_balance === 0 && (
                        <Chip 
                          label="PAID OFF" 
                          color="success" 
                          size="small" 
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          )}

          {/* Penalty Warning */}
          {advancePreview && advancePreview.has_penalty && (
            <Box sx={{ mb: 2 }}>
              <Alert severity="warning">
                <Typography variant="body2">
                  Submitting this amount ({formatCurrency(allocation.amount_for_advance_payment)}) will lead to a penalty of {formatCurrency(advancePreview.penalty_amount)}.
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Minimum required amount to avoid penalty: {formatCurrency(advancePreview.minimum_required_amount)}
                </Typography>
              </Alert>
            </Box>
          )}

          {/* Advance Payment Error */}
          {advancePreview && !advancePreview.is_valid && (
            <Box sx={{ mb: 2 }}>
              <Alert severity="error">
                {advancePreview.message}
              </Alert>
            </Box>
          )}

          <Divider sx={{ my: 3 }} />

          {/* Others section */}
          <Others onOthersChange={handleOthersChange} />

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle1">
              Total Allocated: {formatCurrency(totalAllocated)}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: remaining < 0 ? 'error.main' : remaining > 0 ? 'warning.main' : 'success.main'
              }}
            >
              Remaining: {formatCurrency(remaining)}
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!validateForm() || submitting}
          >
            {submitting ? 'Allocating...' : 'Allocate Funds'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Performance Warning Dialog */}
      <PerformanceWarningDialog
        open={performanceWarning.showWarning}
        onClose={handleCancelWarning}
        onContinue={handleContinueAfterWarning}
        warningData={performanceWarning.warningData}
      />
    </>
  );
};

export default AllocateFundsDialog;